import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, RefreshCw, Users, Shield, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

// Stały adres backendu - NIE zmieniać
const API_BASE = "https://gabka-ostatni-to-ten-napewno2.onrender.com";

export const AdminUsersPage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    if (!token) return;
    
    setError(null);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API_BASE}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!statsRes.ok) {
        throw new Error(`Stats: ${statsRes.status} ${statsRes.statusText}`);
      }
      const statsData = await statsRes.json();
      setStats(statsData);

      // Fetch users
      const usersRes = await fetch(`${API_BASE}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!usersRes.ok) {
        throw new Error(`Users: ${usersRes.status} ${usersRes.statusText}`);
      }
      const usersData = await usersRes.json();
      setUsers(usersData.users || usersData || []);
      
    } catch (e) {
      setError(e.message || 'Błąd pobierania danych');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!authLoading && token && user?.is_admin) {
      fetchData();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [authLoading, token, user]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Auth loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'transparent' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Sprawdzam uprawnienia...</p>
        </div>
      </div>
    );
  }

  // Not logged in - redirect to login
  if (!token || !user) {
    navigate('/auth');
    return null;
  }

  // Not admin
  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'transparent' }}>
        <Card style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)' }} className="border-0">
          <CardContent className="py-12 text-center">
            <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Brak uprawnień administratora.</h2>
            <p className="text-slate-400 mb-6">Ta strona jest dostępna tylko dla administratorów.</p>
            <Button onClick={() => navigate('/')} className="bg-sky-500 hover:bg-sky-600">
              Wróć na stronę główną
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading data
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'transparent' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Ładowanie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ background: 'transparent' }}>
      {/* Header */}
      <div className="sticky top-0 z-10 px-6 py-4" style={{ background: 'rgba(5,13,24,0.95)', backdropFilter: 'blur(12px)' }}>
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/admin')} className="flex items-center text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" /> Panel admina
          </button>
          <Button 
            onClick={handleRefresh} 
            disabled={refreshing}
            variant="outline" 
            size="sm"
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Odśwież
          </Button>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="w-7 h-7 text-sky-400" />
            Lista użytkowników
          </h1>
          {stats && (
            <p className="text-slate-400 mt-1">
              Użytkownicy: <span className="text-white font-semibold">{stats.total_users}</span>
            </p>
          )}
        </div>

        {/* Error */}
        {error && (
          <Card className="mb-6 border-red-500/50" style={{ background: 'rgba(239,68,68,0.1)' }}>
            <CardContent className="py-4">
              <p className="text-red-400 text-sm">Błąd: {error}</p>
            </CardContent>
          </Card>
        )}

        {/* Users table/list */}
        <Card style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(8px)' }} className="border-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Email</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">ID</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Admin</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Data rejestracji</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-slate-500">
                      Brak użytkowników do wyświetlenia
                    </td>
                  </tr>
                ) : (
                  users.map((u, idx) => (
                    <tr 
                      key={u.id || u._id || idx} 
                      className="border-b border-slate-700/50 hover:bg-white/5 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <span className="text-white text-sm">{u.email}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-slate-400 text-xs font-mono">{u.id || u._id || '-'}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {u.is_admin ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400">
                            <Shield className="w-3 h-3 mr-1" />
                            Tak
                          </span>
                        ) : (
                          <span className="text-slate-500 text-xs">Nie</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {u.created_at ? (
                          <span className="text-slate-400 text-xs flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(u.created_at).toLocaleDateString('pl-PL')}
                          </span>
                        ) : (
                          <span className="text-slate-500 text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Summary */}
        {users.length > 0 && (
          <p className="text-center text-slate-500 text-xs mt-4">
            Wyświetlono {users.length} użytkowników
          </p>
        )}
      </div>
    </div>
  );
};
